import {
  AccountSearchableField,
  FlagCategory,
  FlagPriority,
  FlagStates,
  FlagTypes,
} from '@generated/graphql';
import type { SysTokens } from '@ovComponents/0-tokens/sys/sysTokens';
import type { Tokens } from '@ovComponents/0-tokens/useTokens';
import dayjs from 'dayjs';
import type { TFunction } from 'i18next';
import type { ReconFlagSeverity } from './reconFlags.types';

export const PAGE_SIZE = 15;

/** Min characters before calling `searchAccounts` (OpenSearch). */
export const RECON_FLAGS_MIN_SEARCH_LENGTH = 2;

/** Debounce for the account/client search input (ms). */
export const RECON_FLAGS_SEARCH_DEBOUNCE_MS = 400;

/**
 * Max accounts resolved from client search and sent as `accountIds` on `fetchFlagsForGrid`.
 * Must stay aligned with flag-service `MAX_ACCOUNT_IDS` (validation rejects more than 50).
 * Flags on accounts ranked below the top `RECON_FLAGS_MAX_ACCOUNT_IDS` OpenSearch hits are not shown — users should narrow search.
 */
export const RECON_FLAGS_MAX_ACCOUNT_IDS = 50;

/** OpenSearch fields for the recon flags client/account search input. */
export const RECON_FLAGS_ACCOUNT_SEARCH_QUERY_FIELDS = [
  AccountSearchableField.UserFirstName,
  AccountSearchableField.UserLastName,
  AccountSearchableField.UserEntityName,
] as const;

/** Relevance sort for `searchAccounts` (best matches first). */
export const RECON_FLAGS_ACCOUNT_SEARCH_SORT_FIELD = '_score' as const;

export const SPOTLIGHT_TYPES = [
  {
    type: FlagTypes.NegativeCash,
    titleKey: 'reconFlags.spotlight.negativeCash.title',
    colorKey: 'negative' as const,
  },
  {
    type: FlagTypes.NegativeHolding,
    titleKey: 'reconFlags.spotlight.negativeHolding.title',
    colorKey: 'warning' as const,
  },
  {
    type: FlagTypes.DifferentHoldingsV2,
    titleKey: 'reconFlags.spotlight.holdingsMismatch.title',
    colorKey: 'supportThree' as const,
  },
  {
    type: FlagTypes.MoneyAvailableValueIncorrectV2,
    titleKey: 'reconFlags.spotlight.moneyAvailableMismatch.title',
    colorKey: 'positive' as const,
  },
] as const;

export type SpotlightCardColorKey =
  (typeof SPOTLIGHT_TYPES)[number]['colorKey'];

/** Maps KPI spotlight palette keys to `SnapshotCard` tone tokens. */
export const spotlightColorKeyToSnapshotTone = (
  colorKey: SpotlightCardColorKey,
): 'positive' | 'negative' | 'warning' | 'primary' => {
  switch (colorKey) {
    case 'negative':
      return 'negative';
    case 'warning':
      return 'warning';
    case 'supportThree':
      return 'primary';
    case 'positive':
      return 'positive';
    default:
      return 'primary';
  }
};

/** Maps BE `FlagCategory` → `reconFlags.categories.*` i18n key (display only). */
export const FLAG_CATEGORY_TO_I18N: Record<FlagCategory, string> = {
  [FlagCategory.Activity]: 'activity',
  [FlagCategory.Warning]: 'warning',
  [FlagCategory.CashHoldings]: 'cashHoldings',
  [FlagCategory.Snapshot]: 'snapshot',
  [FlagCategory.Transfer]: 'transfer',
  [FlagCategory.Account]: 'account',
  [FlagCategory.Product]: 'product',
  [FlagCategory.Unknown]: 'activity',
};

/** Title-cases `SCREAMING_SNAKE_CASE` when no `reconFlags.flagTypes.*` entry exists. */
const formatFlagTypeFallbackLabel = (flagType: string): string =>
  flagType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');

/** Human-readable label for a `FlagTypes` enum value (`reconFlags.flagTypes.*`). */
export const getReconFlagTypeLabel = (t: TFunction, flagType: string): string =>
  t(`reconFlags.flagTypes.${flagType}`, {
    defaultValue: formatFlagTypeFallbackLabel(flagType),
  });

/** Sort flag type enum values A–Z by their localized filter label. */
export const sortReconFlagTypesByLabel = (
  t: TFunction,
  flagTypes: readonly string[],
): string[] =>
  [...flagTypes].sort((a, b) =>
    getReconFlagTypeLabel(t, a).localeCompare(getReconFlagTypeLabel(t, b)),
  );

/** Human-readable label for a `FlagStates` enum value (`reconFlags.flagState.*`). */
export const getReconFlagStateLabel = (t: TFunction, state: string): string =>
  t(`reconFlags.flagState.${state}`, { defaultValue: state });

export const RECON_FLAG_STATE_FILTER_VALUES: readonly FlagStates[] = [
  FlagStates.Flagged,
  FlagStates.Fixed,
  FlagStates.Trivial,
  FlagStates.Archived,
];

/** Sort flag state filter values A–Z by their localized label. */
export const sortReconFlagStateFilterValuesByLabel = (
  t: TFunction,
  states: readonly FlagStates[],
): FlagStates[] =>
  [...states].sort((a, b) =>
    getReconFlagStateLabel(t, a).localeCompare(getReconFlagStateLabel(t, b)),
  );

export const FLAG_CATEGORY_FILTER_OPTIONS: readonly {
  value: FlagCategory;
  i18nKey: string;
}[] = [
  { value: FlagCategory.Snapshot, i18nKey: 'snapshot' },
  { value: FlagCategory.Warning, i18nKey: 'warning' },
  { value: FlagCategory.CashHoldings, i18nKey: 'cashHoldings' },
  { value: FlagCategory.Activity, i18nKey: 'activity' },
  { value: FlagCategory.Transfer, i18nKey: 'transfer' },
  { value: FlagCategory.Account, i18nKey: 'account' },
  { value: FlagCategory.Product, i18nKey: 'product' },
];

/** Sort category filter options A–Z by their localized label. */
export const sortReconFlagCategoryFilterOptionsByLabel = (
  t: TFunction,
  options: readonly { value: FlagCategory; i18nKey: string }[],
): { value: FlagCategory; i18nKey: string }[] =>
  [...options].sort((a, b) =>
    t(`reconFlags.categories.${a.i18nKey}`).localeCompare(
      t(`reconFlags.categories.${b.i18nKey}`),
    ),
  );

/** Uses `Flag.priority` from the row for badge colors (no inference from `type`). */
export const badgeSeverityFromPriority = (
  p: FlagPriority | null | undefined,
): ReconFlagSeverity => {
  if (p == null || p === FlagPriority.Unknown) {
    return 'MEDIUM';
  }
  return p as ReconFlagSeverity;
};

export const agingDays = (createdAt: string): number =>
  Math.max(0, dayjs().diff(dayjs(createdAt), 'day'));

/** Distinct pill colors for flag type labels (cycles deterministically by type string). */
export type ReconFlagTypeBadgeColor =
  | 'supportOne'
  | 'supportTwo'
  | 'supportThree'
  | 'supportFour'
  | 'supportFive'
  | 'supportSix';

const TYPE_BADGE_COLOR_CYCLE: readonly ReconFlagTypeBadgeColor[] = [
  'supportOne',
  'supportTwo',
  'supportThree',
  'supportFour',
  'supportFive',
  'supportSix',
];

export const badgeColorForFlagType = (
  type: string,
): ReconFlagTypeBadgeColor => {
  let h = 0;
  for (let i = 0; i < type.length; i++) {
    h = (h * 31 + type.charCodeAt(i)) | 0;
  }
  const idx = Math.abs(h) % TYPE_BADGE_COLOR_CYCLE.length;
  return TYPE_BADGE_COLOR_CYCLE[idx] ?? 'supportOne';
};

/**
 * Border + text color for inactive severity filter pills (`SeverityFilterPill`).
 * Use for grid/drawer priority chips so they match the filter row.
 */
export const severityFilterAccentColor = (
  sys: Tokens['sys'],
  severity: ReconFlagSeverity,
): string => {
  switch (severity) {
    case 'CRITICAL':
      return String(sys.color.negative);
    case 'HIGH':
      return String(sys.color.warning);
    case 'MEDIUM':
      return String(sys.color.supportThreeOnVariant ?? sys.color.warning);
    case 'LOW':
      return String(sys.color.positive);
    default: {
      const _exhaustive: never = severity;
      return _exhaustive;
    }
  }
};

/** Same surface + outlined pill as inactive severity filters (border and text = accent). */
export const getReconFlagPriorityBadgeSx = (
  sys: Tokens['sys'],
  severity: ReconFlagSeverity,
) => {
  const accent = severityFilterAccentColor(sys, severity);
  return {
    color: accent,
    backgroundColor: String(sys.color.surface),
    borderColor: accent,
    borderStyle: 'solid' as const,
    borderWidth: '1px',
  };
};

/** State pills: flagged = critical accent; fixed/trivial (“done”) = low; else outline. */
export const getReconFlagStateBadgeSx = (sys: Tokens['sys'], state: string) => {
  if (state === FlagStates.Flagged) {
    return getReconFlagPriorityBadgeSx(sys, 'CRITICAL');
  }
  if (
    state === FlagStates.Fixed ||
    state === FlagStates.Trivial ||
    state === FlagStates.Archived
  ) {
    return getReconFlagPriorityBadgeSx(sys, 'LOW');
  }
  const accent = String(sys.color.outline);
  return {
    color: accent,
    backgroundColor: String(sys.color.surface),
    borderColor: accent,
    borderStyle: 'solid' as const,
    borderWidth: '1px',
  };
};

const spotlightGradient = (from: string, to: string): string =>
  `linear-gradient(135deg, ${from} 0%, ${to} 150%)`;

/**
 * Full `background` value for KPI spotlight cards (overrides flat card fill).
 * Stops follow semantic palette main → darker for left-to-right severity styling.
 */
export const spotlightCardBackground = (
  sys: SysTokens,
  colorKey: SpotlightCardColorKey,
): string => {
  const surface = sys.color.surfaceContainer ?? sys.color.surface ?? '#ffffff';

  switch (colorKey) {
    case 'negative': {
      const from = sys.color.negative ?? surface;
      const to = sys.color.negativePressed ?? sys.color.negative ?? from;
      return spotlightGradient(from, to);
    }
    case 'warning': {
      const from = sys.color.warning ?? surface;
      const to = sys.color.warningPressed ?? sys.color.warning ?? from;
      return spotlightGradient(from, to);
    }
    case 'supportThree': {
      const from = sys.color.supportThree ?? sys.color.warning ?? surface;
      const to =
        sys.color.supportThreeOnVariant ??
        sys.color.warningPressed ??
        sys.color.warning ??
        from;
      return spotlightGradient(from, to);
    }
    case 'positive': {
      const from = sys.color.positive ?? surface;
      const to = sys.color.positivePressed ?? sys.color.positive ?? from;
      return spotlightGradient(from, to);
    }
    default:
      return spotlightGradient(surface, surface);
  }
};

export const spotlightOnColor = (
  sys: SysTokens,
  colorKey: SpotlightCardColorKey,
): string | undefined => {
  switch (colorKey) {
    case 'negative':
      return sys.color.onNegative ?? sys.color.onSurface;
    case 'warning':
      return sys.color.onWarning ?? sys.color.onSurface;
    case 'supportThree':
      return sys.color.onWarning ?? sys.color.onSurface;
    case 'positive':
      return sys.color.onPositive ?? sys.color.onSurface;
    default:
      return sys.color.onSurface;
  }
};
